/**
 * FinanceApp - Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 *
 * CustomerForm — Fiche client enrichie (4 groupes, style V3)
 * Props :
 *   customerId  {number|null}  — undefined = création, id = édition
 *   onSaved     {Function}     — appelé après succès
 *   onCancel    {Function}     — appelé sur annulation
 */
import { useState, useEffect } from 'react';
import api from '../../services/api';

// ── Constantes ──────────────────────────────────────────────────
const LEGAL_FORMS    = ['SAS','SARL','SA','EURL','SCI','SNC','SCOP','SASU','Auto-entrepreneur','Association loi 1901','Fondation','GIE','Autre'];
const PAYMENT_TERMS  = ['comptant','30j','45j','60j','30j_fin_mois','45j_fin_mois'];
const CURRENCIES     = ['EUR','USD','GBP','CHF','MAD','TND','DZD','XOF'];
const LANGUAGES      = ['fr','en','es','de','it','ar','nl'];
const CIVILITIES     = ['M.','Mme','Dr','Me'];
const COMPANY_SIZES  = ['1','2-10','11-50','51-250','251-1000','1000+'];
const PRICE_TIERS    = ['Standard','Préférentiel','Grand compte','Personnalisé'];
const PAY_METHODS    = ['Virement bancaire','Prélèvement SEPA','Chèque','Carte bancaire','LCR','Espèces'];
const BANK2_USAGES   = ['Salaires','Fournisseurs','Remboursements','Autre'];
const CONTACT2_TYPES = ['Décideur','Prescripteur','Utilisateur','Payeur','Destinataire factures'];
const STATUSES       = ['prospect','client_actif','client_inactif','partenaire','fournisseur'];
const VAT_EXEMPTIONS = ['non','oui','auto','exonere'];
const SEPA_TYPES     = ['Récurrent','Ponctuel'];
const SEPA_STATUSES  = ['actif','suspendu','résilié'];

// ── 4 groupes (au lieu de 8), couleurs alignées sur la fiche lecture V3 ──
const SECTIONS = [
  { id: 0, label: 'Identité',              color: '#185fa5' },
  { id: 1, label: 'Adresse & Légal',       color: '#ba7517' },
  { id: 2, label: 'Banque & Commercial',   color: '#6366f1' },
  { id: 3, label: 'Contacts & Facturation',color: '#1a9e72' },
];

const EMPTY = {
  name:'', trade_name:'', dolibarr_account_code:'', brand_name:'', company_size:'', status:'client_actif',
  email:'', email_billing:'', phone:'', phone2:'', fax:'', website:'', sector:'', notes:'',
  address:'', address2:'', postal_code:'', city:'', department:'', country:'FR',
  delivery_address1:'', delivery_address2:'', delivery_postal_code:'', delivery_city:'',
  delivery_country:'', delivery_instructions:'',
  billing_address1:'', billing_address2:'', billing_postal_code:'', billing_city:'',
  billing_country:'', billing_ref:'',
  legal_form:'', creation_date:'', siren:'', siret:'', tva_number:'', naf_code:'',
  rcs_number:'', tahiti_number:'', share_capital:'', associates_count:'',
  greffe:'', rcs_date:'', corporate_purpose:'', insurer:'', insurance_policy:'',
  bank_name:'', bank_iban:'', bank_bic:'', bank_domiciliation:'',
  bank_account_holder:'', bank_account_number:'',
  bank2_name:'', bank2_iban:'', bank2_bic:'', bank2_usage:'',
  sepa_mandate_ref:'', sepa_mandate_date:'', sepa_mandate_type:'', sepa_mandate_status:'',
  payment_terms:'', payment_delay_days:'', credit_limit:'', discount_rate:'',
  early_payment_discount:'', late_penalty_rate:'',
  currency:'EUR', invoice_language:'fr', payment_method:'', price_tier:'',
  vat_exemption:'non', default_vat_rate:'', commercial_notes:'',
  contact_civility:'', contact_firstname:'', contact_lastname:'', contact_role:'',
  contact_email:'', contact_phone:'', contact_mobile:'', contact_linkedin:'',
  contact2_civility:'', contact2_firstname:'', contact2_lastname:'', contact2_role:'',
  contact2_email:'', contact2_phone:'', contact2_mobile:'', contact2_type:'',
  billing_contact_name:'', billing_contact_email:'', billing_contact_phone:'',
  billing_po_ref:'', billing_instructions:'',
};

// Petit sous-titre de bloc à l'intérieur d'un groupe (remplace les anciens onglets fusionnés)
const SubHeader = ({ children, first }) => (
  <div style={{ fontWeight:700, fontSize:12, color:'var(--text-muted)', margin: first ? '0 0 10px' : '22px 0 10px', textTransform:'uppercase', paddingTop: first ? 0 : 14, borderTop: first ? 'none' : '1px solid #eeece6' }}>
    {children}
  </div>
);

// ── Composant ───────────────────────────────────────────────────
export default function CustomerForm({ customerId, onSaved, onCancel }) {
  const [form, setForm]       = useState(EMPTY);
  const [section, setSection] = useState(0);
  const [loading, setLoading] = useState(!!customerId);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  // IBAN masqués en édition — saisie brute séparée
  const [ibanMasked,  setIbanMasked]  = useState('');
  const [iban2Masked, setIban2Masked] = useState('');
  const [ibanEdit,    setIbanEdit]    = useState(false);
  const [iban2Edit,   setIban2Edit]   = useState(false);
  const [ibanRaw,     setIbanRaw]     = useState('');
  const [iban2Raw,    setIban2Raw]    = useState('');
  // Recherche SIRENE
  const [sirenLookupNumber, setSirenLookupNumber] = useState('');
  const [sirenLookupLoading, setSirenLookupLoading] = useState(false);
  const [sirenLookupError, setSirenLookupError] = useState('');
  const [sirenLookupSuccess, setSirenLookupSuccess] = useState('');
  const [sirenLookupClosed, setSirenLookupClosed] = useState(null); // date de fermeture si entreprise fermée
  // Recherche SIRENE par nom (résultats multiples)
  const [nameQuery, setNameQuery] = useState('');
  const [nameResults, setNameResults] = useState([]);
  const [nameSearchLoading, setNameSearchLoading] = useState(false);
  const [nameSearchError, setNameSearchError] = useState('');

  const isEdit = !!customerId;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Recherche SIRENE (auto-complétion depuis un SIREN ou SIRET) ─────
  const doSirenLookup = async () => {
    const number = (sirenLookupNumber || form.siret || form.siren || '').replace(/\D/g, '');
    if (number.length !== 9 && number.length !== 14) {
      setSirenLookupError('Saisir un SIREN (9 chiffres) ou un SIRET (14 chiffres)');
      return;
    }
    setSirenLookupLoading(true); setSirenLookupError(''); setSirenLookupClosed(null); setSirenLookupSuccess('');
    try {
      const r = await api.customers.sirenLookup(number);
      setForm(f => ({
        ...f,
        name: r.name || f.name,
        address: r.address || f.address,
        postal_code: r.postal_code || f.postal_code,
        city: r.city || f.city,
        country: r.country || f.country,
        naf_code: r.naf_code || f.naf_code,
        legal_form: r.legal_form || f.legal_form,
        creation_date: r.creation_date || f.creation_date,
        siren: r.siren || f.siren,
        siret: r.siret || f.siret,
        tva_number: r.tva_number || f.tva_number,
      }));
      if (r.is_active === false) setSirenLookupClosed(r.date_fermeture);
      setSirenLookupSuccess(`✓ Fiche complétée depuis Sirene — ${r.name}`);
    } catch (err) {
      setSirenLookupError(err.message || 'Entreprise introuvable');
    } finally {
      setSirenLookupLoading(false);
    }
  };

  // ── Recherche SIRENE par nom (raison sociale) — plusieurs résultats ──
  const doNameSearch = async () => {
    if (nameQuery.trim().length < 2) {
      setNameSearchError('Saisir au moins 2 caractères');
      return;
    }
    setNameSearchLoading(true); setNameSearchError(''); setNameResults([]);
    try {
      const r = await api.customers.sirenSearch(nameQuery.trim());
      setNameResults(r.results || []);
      if (!r.results || r.results.length === 0) setNameSearchError('Aucun résultat pour cette recherche');
    } catch (err) {
      setNameSearchError(err.message || 'Erreur de recherche');
    } finally {
      setNameSearchLoading(false);
    }
  };

  const selectNameResult = (r) => {
    setForm(f => ({
      ...f,
      name: r.name || f.name,
      address: r.address || f.address,
      postal_code: r.postal_code || f.postal_code,
      city: r.city || f.city,
      country: r.country || f.country,
      naf_code: r.naf_code || f.naf_code,
      legal_form: r.legal_form || f.legal_form,
      creation_date: r.creation_date || f.creation_date,
      siren: r.siren || f.siren,
      siret: r.siret || f.siret,
      tva_number: r.tva_number || f.tva_number,
    }));
    setSirenLookupClosed(r.is_active === false ? r.date_fermeture : null);
    setSirenLookupSuccess(`✓ Fiche complétée depuis Sirene — ${r.name}`);
    setNameResults([]);
    setNameQuery('');
  };

  // ── Charger en mode édition ──────────────────────────────────
  useEffect(() => {
    if (!customerId) return;
    api.customers.get(customerId)
      .then(data => {
        const { bank_iban_masked, bank2_iban_masked, ...rest } = data;
        setIbanMasked(bank_iban_masked || '');
        setIban2Masked(bank2_iban_masked || '');
        const filled = { ...EMPTY };
        Object.keys(filled).forEach(k => {
          if (k !== 'bank_iban' && k !== 'bank2_iban' && rest[k] != null) {
            filled[k] = rest[k];
          }
        });
        setForm(filled);
      })
      .catch(e => setError(e.message || 'Impossible de charger le client.'))
      .finally(() => setLoading(false));
  }, [customerId]);

  // ── Soumission ──────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const payload = { ...form };

      ['share_capital','associates_count','payment_delay_days','credit_limit',
       'discount_rate','early_payment_discount','late_penalty_rate','default_vat_rate'].forEach(k => {
        if (payload[k] === '' || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });

      if (ibanEdit && ibanRaw) payload.bank_iban = ibanRaw.replace(/\s/g,'').toUpperCase();
      else delete payload.bank_iban;
      if (iban2Edit && iban2Raw) payload.bank2_iban = iban2Raw.replace(/\s/g,'').toUpperCase();
      else delete payload.bank2_iban;

      if (isEdit) await api.customers.update(customerId, payload);
      else await api.customers.create(payload);
      onSaved();
    } catch (err) {
      setError(err.message || "Erreur lors de l'enregistrement.");
    } finally { setSaving(false); }
  };

  // ── Helpers UI ──────────────────────────────────────────────
  const inp = (key, props = {}) => (
    <input className="form-control" value={form[key] ?? ''} onChange={e => set(key, e.target.value)} {...props} />
  );
  const sel = (key, options, props = {}) => (
    <select className="form-control" value={form[key] ?? ''} onChange={e => set(key, e.target.value)} {...props}>
      <option value="">— Choisir —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  const grp = (label, child, hint) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {child}
      {hint && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>{hint}</div>}
    </div>
  );
  const ibanField = (label, masked, showEdit, onEdit, raw, onRaw) => grp(label,
    showEdit || !masked ? (
      <input className="form-control" value={raw}
        onChange={e => onRaw(e.target.value.replace(/\s/g,'').toUpperCase())}
        placeholder="FR7630004000031234567890143"
        autoComplete="off" style={{ fontFamily:'monospace', letterSpacing:1 }} />
    ) : (
      <div style={{ display:'flex', gap:8 }}>
        <input className="form-control" value={masked} readOnly style={{ flex:1, fontFamily:'monospace', color:'#185fa5', letterSpacing:2 }} />
        <button type="button" className="btn sm" onClick={onEdit}>Modifier</button>
      </div>
    ),
    'Saisir sans espaces — masqué après enregistrement'
  );

  const sirenOk  = !form.siren  || /^\d{9}$/.test(form.siren);
  const siretOk  = !form.siret  || /^\d{14}$/.test(form.siret);

  if (loading) return <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Chargement…</div>;

  return (
    <form onSubmit={handleSubmit}>
      {/* Onglets — 4 groupes, point coloré + soulignement (style V3) */}
      <div style={{ display:'flex', gap:24, borderBottom:'1px solid #e6e5e0', marginBottom:20, overflowX:'auto', flexWrap:'nowrap' }}>
        {SECTIONS.map(s => {
          const active = section === s.id;
          return (
            <button key={s.id} type="button" onClick={() => setSection(s.id)} style={{
              display:'flex', alignItems:'center', gap:6, padding:'11px 2px', fontSize:13,
              fontWeight:700, cursor:'pointer', border:'none', background:'transparent', whiteSpace:'nowrap',
              color: active ? '#161a22' : '#8a8f98',
              borderBottom:`2.5px solid ${active ? s.color : 'transparent'}`, marginBottom:-1,
            }}>
              <span style={{ width:7, height:7, borderRadius:'50%', background: active ? s.color : '#d8d7d0', flexShrink:0 }} />
              {s.label}
            </button>
          );
        })}
      </div>

      {error && <div className="alert danger" style={{ marginBottom:12 }}>{error}</div>}

      {/* ── GROUPE 0 — Identité ──────────────────────────────── */}
      {section === 0 && (
        <div>
          {grp('Raison sociale *', <input className="form-control" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme SAS" />)}
          <div className="form-row">
            {grp("Nom commercial", inp('trade_name', { placeholder:'ex: Acme Pro' }))}
            {grp("Enseigne / marque", inp('brand_name'))}
          </div>
          {/* Code comptable de l'expert-comptable (28/10/2026) : facultatif (reprise d'activité) */}
          {grp("Code comptable (expert-comptable)", <>
            <input className="form-control" value={form.dolibarr_account_code || ''} maxLength={20} placeholder="ex. 411CARBL"
              onChange={e => set('dolibarr_account_code', e.target.value.toUpperCase().replace(/\s/g, ''))} />
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>Le compte client chez votre expert-comptable (ex. 411CARBL), pour une société en « reprise d'activité ». Vide : compte automatique, code à attribuer plus tard (Plan comptable > Codes tiers).</div>
          </>)}
          <div className="form-row">
            {grp('Statut', sel('status', STATUSES))}
            {grp('Taille', sel('company_size', COMPANY_SIZES))}
          </div>
          <div className="form-row">
            {grp('Email principal', inp('email', { type:'email', autoComplete:'off' }))}
            {grp('Email facturation', inp('email_billing', { type:'email', autoComplete:'off' }))}
          </div>
          <div className="form-row">
            {grp('Téléphone', inp('phone', { placeholder:'+33 1 23 45 67 89' }))}
            {grp('Téléphone 2', inp('phone2'))}
            {grp('Fax', inp('fax'))}
          </div>
          {grp('Site web', inp('website', { type:'url', placeholder:'https://exemple.com' }))}
          {grp("Secteur d'activité", inp('sector', { placeholder:"ex: Informatique, BTP, Santé…" }))}
          {grp('Notes', <textarea className="form-control" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />)}
        </div>
      )}

      {/* ── GROUPE 1 — Adresse & Légal (fusion) ──────────────── */}
      {section === 1 && (
        <div>
          <SubHeader first>Siège social</SubHeader>
          {grp('Adresse', inp('address'))}
          {grp('Complément', inp('address2', { placeholder:"Bât. A, ZI, BP…" }))}
          <div className="form-row">
            {grp('Code postal', inp('postal_code'))}
            {grp('Ville', inp('city'))}
            {grp('Département', inp('department'))}
            {grp('Pays', <input className="form-control" value={form.country} maxLength={2} onChange={e => set('country', e.target.value.toUpperCase())} placeholder="FR" />)}
          </div>

          <SubHeader>Adresse de livraison</SubHeader>
          {grp('Adresse 1', inp('delivery_address1'))}
          {grp('Adresse 2', inp('delivery_address2'))}
          <div className="form-row">
            {grp('CP livraison', inp('delivery_postal_code'))}
            {grp('Ville livraison', inp('delivery_city'))}
            {grp('Pays livraison', <input className="form-control" value={form.delivery_country} maxLength={2} onChange={e => set('delivery_country', e.target.value.toUpperCase())} placeholder="FR" />)}
          </div>
          {grp('Instructions livraison', inp('delivery_instructions', { placeholder:"Horaires, code porte, contact…" }))}

          <SubHeader>Adresse de facturation</SubHeader>
          {grp('Adresse 1', inp('billing_address1'))}
          {grp('Adresse 2', inp('billing_address2'))}
          <div className="form-row">
            {grp('CP facturation', inp('billing_postal_code'))}
            {grp('Ville facturation', inp('billing_city'))}
            {grp('Pays facturation', <input className="form-control" value={form.billing_country} maxLength={2} onChange={e => set('billing_country', e.target.value.toUpperCase())} placeholder="FR" />)}
          </div>
          {grp('Référence bon de commande', inp('billing_ref', { placeholder:"BC-2026-001" }))}

          <SubHeader>Informations légales</SubHeader>

          <div style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:16, padding:'12px 14px', background:'#f9fafb', borderRadius:8, border:'1px solid #e6e5e0' }}>
            <div style={{ flex:1 }}>
              <input className="form-control" placeholder="SIREN (9 chiffres) ou SIRET (14 chiffres)"
                value={sirenLookupNumber} onChange={e => setSirenLookupNumber(e.target.value.replace(/\D/g,''))}
                maxLength={14} />
              {sirenLookupError && <div style={{ fontSize:11, color:'#e24b4a', marginTop:4 }}>{sirenLookupError}</div>}
              {sirenLookupSuccess && !sirenLookupError && <div style={{ fontSize:11, color:'#1a9e72', marginTop:4, fontWeight:600 }}>{sirenLookupSuccess}</div>}
              {sirenLookupClosed !== null && (
                <div style={{ fontSize:11, color:'#ba7517', marginTop:4, fontWeight:600 }}>
                  ⚠️ Entreprise déclarée fermée{sirenLookupClosed ? ` depuis le ${new Date(sirenLookupClosed).toLocaleDateString('fr-FR')}` : ''} — vérifier avant de facturer.
                </div>
              )}
            </div>
            <button type="button" className="btn primary" disabled={sirenLookupLoading} onClick={doSirenLookup} style={{ whiteSpace:'nowrap' }}>
              {sirenLookupLoading ? 'Recherche…' : '🔍 Rechercher (Sirene)'}
            </button>
          </div>

          <div style={{ marginBottom:16, padding:'12px 14px', background:'#f9fafb', borderRadius:8, border:'1px solid #e6e5e0' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:8 }}>Vous ne connaissez pas le SIREN/SIRET ? Recherchez par raison sociale :</div>
            <div style={{ display:'flex', gap:8 }}>
              <input className="form-control" placeholder="ex: Acme Distribution" value={nameQuery}
                onChange={e => setNameQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); doNameSearch(); } }} />
              <button type="button" className="btn" disabled={nameSearchLoading} onClick={doNameSearch} style={{ whiteSpace:'nowrap' }}>
                {nameSearchLoading ? 'Recherche…' : '🔍 Rechercher par nom'}
              </button>
            </div>
            {nameSearchError && <div style={{ fontSize:11, color:'#e24b4a', marginTop:6 }}>{nameSearchError}</div>}
            {nameResults.length > 0 && (
              <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:6, maxHeight:260, overflowY:'auto' }}>
                {nameResults.map((r, i) => (
                  <div key={r.siren || i} onClick={() => selectNameResult(r)}
                    style={{ padding:'8px 10px', background:'#fff', border:'1px solid #e6e5e0', borderRadius:7, cursor:'pointer', fontSize:12.5 }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#6366f1'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e6e5e0'}>
                    <div style={{ fontWeight:700, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      {r.name}
                      {r.is_active === false && <span style={{ fontSize:10, fontWeight:700, color:'#ba7517', background:'#ba751718', padding:'1px 6px', borderRadius:6 }}>fermée</span>}
                    </div>
                    <div style={{ color:'var(--text-muted)', fontSize:11, marginTop:2 }}>
                      {[r.postal_code, r.city].filter(Boolean).join(' ')} · SIREN {r.siren}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-row">
            {grp('Forme juridique', sel('legal_form', LEGAL_FORMS))}
            {grp('Date de création', inp('creation_date', { type:'date' }))}
          </div>
          <div className="form-row">
            {grp(<>SIREN <span style={{ fontSize:10, color:'var(--text-muted)' }}>(9 chif.)</span></>,
              <input className="form-control" value={form.siren} maxLength={9}
                onChange={e => set('siren', e.target.value.replace(/\D/g,''))}
                style={{ borderColor: !sirenOk ? '#e24b4a' : undefined }} />,
              !sirenOk ? '9 chiffres requis' : ''
            )}
            {grp(<>SIRET <span style={{ fontSize:10, color:'var(--text-muted)' }}>(14 chif.)</span></>,
              <input className="form-control" value={form.siret} maxLength={14}
                onChange={e => set('siret', e.target.value.replace(/\D/g,''))}
                style={{ borderColor: !siretOk ? '#e24b4a' : undefined }} />,
              !siretOk ? '14 chiffres requis' : ''
            )}
          </div>
          <div className="form-row">
            {grp('N° TVA intracommunautaire', <input className="form-control" value={form.tva_number} onChange={e => set('tva_number', e.target.value.toUpperCase())} placeholder="FR12345678901" maxLength={20} />)}
            {grp(<>Code NAF <span style={{ fontSize:10, color:'var(--text-muted)' }}>(ex: 6201Z)</span></>,
              <input className="form-control" value={form.naf_code} maxLength={5} onChange={e => set('naf_code', e.target.value.toUpperCase())} placeholder="6201Z" />
            )}
          </div>
          <div className="form-row">
            {grp('N° RCS', inp('rcs_number', { placeholder:'RCS Paris B 123 456 789', maxLength:50 }))}
            {grp('Greffe', inp('greffe', { placeholder:"Greffe de Paris", maxLength:100 }))}
            {grp('Date RCS', inp('rcs_date', { type:'date' }))}
          </div>
          <div className="form-row">
            {grp('Capital social (€)', inp('share_capital', { type:'number', min:0, step:100 }))}
            {grp("Nombre d'associés", inp('associates_count', { type:'number', min:1 }))}
            {grp('N° Tahiti', inp('tahiti_number', { maxLength:20 }))}
          </div>
          {grp("Objet social", <textarea className="form-control" rows={2} value={form.corporate_purpose} onChange={e => set('corporate_purpose', e.target.value)} />)}
          <div className="form-row">
            {grp('Assureur RC Pro', inp('insurer', { maxLength:200 }))}
            {grp('N° Police', inp('insurance_policy', { maxLength:100 }))}
          </div>
        </div>
      )}

      {/* ── GROUPE 2 — Banque & Commercial (fusion) ──────────── */}
      {section === 2 && (
        <div>
          <div style={{ padding:'8px 12px', background:'#185fa510', border:'1px solid #185fa530', borderRadius:8, marginBottom:16, fontSize:12, color:'#185fa5', display:'flex', gap:8, alignItems:'center' }}>
            🔒 Données chiffrées AES-256-GCM — IBAN stocké chiffré, jamais transmis en clair
          </div>

          <SubHeader first>Banque principale</SubHeader>
          <div className="form-row">
            {grp('Nom de la banque', inp('bank_name', { placeholder:'BNP Paribas', maxLength:100 }))}
            {grp('Titulaire du compte', inp('bank_account_holder', { maxLength:200 }))}
          </div>
          {ibanField('IBAN', ibanMasked, ibanEdit, () => setIbanEdit(true), ibanRaw, setIbanRaw)}
          <div className="form-row">
            {grp('BIC/SWIFT', <input className="form-control" value={form.bank_bic} onChange={e => set('bank_bic', e.target.value.toUpperCase())} placeholder="BNPAFRPPXXX" maxLength={11} />)}
            {grp('Domiciliation bancaire', inp('bank_domiciliation', { maxLength:200 }))}
            {grp('N° de compte', inp('bank_account_number', { maxLength:50 }))}
          </div>

          <SubHeader>Banque secondaire (optionnel)</SubHeader>
          <div className="form-row">
            {grp('Nom', inp('bank2_name', { maxLength:100 }))}
            {grp('Usage', sel('bank2_usage', BANK2_USAGES))}
          </div>
          {ibanField('IBAN 2', iban2Masked, iban2Edit, () => setIban2Edit(true), iban2Raw, setIban2Raw)}
          <div className="form-row">
            {grp('BIC 2', <input className="form-control" value={form.bank2_bic} onChange={e => set('bank2_bic', e.target.value.toUpperCase())} placeholder="BNPAFRPPXXX" maxLength={11} />)}
          </div>

          <SubHeader>Mandat SEPA</SubHeader>
          <div className="form-row">
            {grp('Référence mandat', inp('sepa_mandate_ref', { maxLength:100 }))}
            {grp('Date signature', inp('sepa_mandate_date', { type:'date' }))}
          </div>
          <div className="form-row">
            {grp('Type', sel('sepa_mandate_type', SEPA_TYPES))}
            {grp('Statut', sel('sepa_mandate_status', SEPA_STATUSES))}
          </div>

          <SubHeader>Conditions commerciales</SubHeader>
          <div className="form-row">
            {grp('Conditions de paiement', sel('payment_terms', PAYMENT_TERMS))}
            {grp('Délai (jours)', inp('payment_delay_days', { type:'number', min:0, max:180 }))}
          </div>
          <div className="form-row">
            {grp('Mode de paiement', sel('payment_method', PAY_METHODS))}
            {grp('Tarification', sel('price_tier', PRICE_TIERS))}
          </div>
          <div className="form-row">
            {grp('Plafond de crédit (€)', inp('credit_limit', { type:'number', min:0, step:1000 }))}
            {grp('Remise par défaut (%)', inp('discount_rate', { type:'number', min:0, max:100, step:0.5 }))}
          </div>
          <div className="form-row">
            {grp("Escompte paiement anticipé (%)", inp('early_payment_discount', { type:'number', min:0, max:20, step:0.5 }))}
            {grp("Pénalités de retard (%)", inp('late_penalty_rate', { type:'number', min:0, max:20, step:0.5 }))}
          </div>
          <div className="form-row">
            {grp('Devise', sel('currency', CURRENCIES))}
            {grp('Langue des documents', sel('invoice_language', LANGUAGES))}
          </div>
          <div className="form-row">
            {grp('Exonération TVA', sel('vat_exemption', VAT_EXEMPTIONS))}
            {grp('Taux TVA par défaut (%)', sel('default_vat_rate', ['0','2.1','5.5','10','20']))}
          </div>
          {grp('Notes commerciales', <textarea className="form-control" rows={3} value={form.commercial_notes} onChange={e => set('commercial_notes', e.target.value)} />)}
        </div>
      )}

      {/* ── GROUPE 3 — Contacts & Facturation (fusion) ───────── */}
      {section === 3 && (
        <div>
          <SubHeader first>Contact principal</SubHeader>
          <div className="form-row">
            {grp('Civilité', sel('contact_civility', CIVILITIES))}
            {grp('Prénom', inp('contact_firstname', { maxLength:100 }))}
            {grp('Nom', inp('contact_lastname', { maxLength:100 }))}
          </div>
          {grp('Fonction / Rôle', inp('contact_role', { maxLength:100, placeholder:'ex: Directeur Achats' }))}
          <div className="form-row">
            {grp('Email', inp('contact_email', { type:'email', autoComplete:'off' }))}
            {grp('Téléphone direct', inp('contact_phone'))}
            {grp('Mobile', inp('contact_mobile'))}
          </div>
          {grp('LinkedIn', inp('contact_linkedin', { type:'url', placeholder:'https://linkedin.com/in/…', maxLength:500 }))}

          <SubHeader>Contact secondaire</SubHeader>
          <div className="form-row">
            {grp('Civilité', sel('contact2_civility', CIVILITIES))}
            {grp('Prénom', inp('contact2_firstname', { maxLength:100 }))}
            {grp('Nom', inp('contact2_lastname', { maxLength:100 }))}
          </div>
          {grp('Fonction / Rôle', inp('contact2_role', { maxLength:100 }))}
          {grp('Type de contact', sel('contact2_type', CONTACT2_TYPES))}
          <div className="form-row">
            {grp('Email', inp('contact2_email', { type:'email', autoComplete:'off' }))}
            {grp('Téléphone', inp('contact2_phone'))}
            {grp('Mobile', inp('contact2_mobile'))}
          </div>

          <SubHeader>Contact facturation</SubHeader>
          {grp('Nom du contact facturation', inp('billing_contact_name', { maxLength:200, placeholder:'ex: Service Comptabilité' }))}
          <div className="form-row">
            {grp('Email facturation', inp('billing_contact_email', { type:'email', autoComplete:'off' }))}
            {grp('Téléphone facturation', inp('billing_contact_phone'))}
          </div>
          {grp('Référence bon de commande client', inp('billing_po_ref', { maxLength:100, placeholder:'BC-2026-001' }))}
          {grp('Instructions de facturation', <textarea className="form-control" rows={4} value={form.billing_instructions} onChange={e => set('billing_instructions', e.target.value)} placeholder="Mentions obligatoires, adresse email précise, délai de règlement…" />)}
        </div>
      )}

      {/* Boutons */}
      <div className="btn-group" style={{ justifyContent:'space-between', marginTop:20 }}>
        <div style={{ display:'flex', gap:8 }}>
          {section > 0 && <button type="button" className="btn" onClick={() => setSection(s => s - 1)}>← Précédent</button>}
          {section < 3 && <button type="button" className="btn" onClick={() => setSection(s => s + 1)}>Suivant →</button>}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button type="button" className="btn" onClick={onCancel}>Annuler</button>
          <button type="submit" className="btn primary" disabled={saving}>
            {saving ? 'Enregistrement…' : (isEdit ? 'Mettre à jour' : 'Créer le client')}
          </button>
        </div>
      </div>
    </form>
  );
}
